import React, { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';
import StaleAssignmentBanner from './StaleAssignmentBanner';
import { fetchSharedLivingStaff } from 'helpers/sharedLivingApi';
import { isCaregiverRole } from 'helpers/isSharedLivingActive';

/**
 * UI-068 / UI-070 / UI-071: dual single-select pickers for nurse + case-
 * manager on the edit-patient form.
 *
 * Source list: GET /users/list-sharedliving-staff/:groupId (SL-123) returns
 * role-5 staff in the group who have user_facility access to at least one
 * SL-flagged facility. Both pickers share that list.
 *
 * UD3: stale assignment banner shown when the currently-assigned user is
 * is_active = 0. The "Inactive" suffix on the option label also tags the
 * stale row in the dropdown for context.
 *
 * UD5: hidden entirely when the current user is role 7 (caregiver). Backend
 * SL-108 strips these fields for role 7 — these controls would be inert.
 *
 * UD6: `isClearable` on both selects so the admin can null-out without
 * picking a replacement.
 *
 * UD8: any backend validation message provided via `errors.nurse` /
 * `errors.case_manager` is shown inline beneath the offending field.
 *
 * Props:
 *   groupId            number | string  — patient's group id
 *   nurse              user-mini-object or null (current assignment, for stale display)
 *   caseManager        user-mini-object or null
 *   nurseUserId        number | string | null  — controlled current value
 *   caseManagerUserId  number | string | null
 *   onChangeNurse      (id | null) => void
 *   onChangeCaseManager(id | null) => void
 *   errors             optional { nurse?: string, case_manager?: string }
 */
const NurseCasemanagerPickers = ({
  groupId,
  nurse,
  caseManager,
  nurseUserId,
  caseManagerUserId,
  onChangeNurse,
  onChangeCaseManager,
  errors = {}
}) => {
  const [staffOptions, setStaffOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  // UD5: caregivers never see these controls.
  if (isCaregiverRole()) return null;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!groupId) {
        setStaffOptions([]);
        return;
      }
      setLoading(true);
      try {
        const { data } = await fetchSharedLivingStaff(groupId);
        if (cancelled) return;
        const mapped = (Array.isArray(data) ? data : []).map(u => ({
          value: u.id,
          label:
            `${u.first_name || ''} ${u.last_name || ''}`.trim() ||
            u.email ||
            `User #${u.id}`,
          phone: u.phone || ''
        }));
        setStaffOptions(mapped);
      } catch (error) {
        toast.error('Unable to load SharedLiving staff');
        setStaffOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [groupId]);

  // Build the final option list per-picker — if the currently-assigned user
  // isn't in the live list (e.g. they lost access to the SL facility), we
  // still surface them with an "(Inactive)" suffix so the admin can clear
  // them.
  const buildOptions = (current, currentId) => {
    if (!current) return staffOptions;
    const id = String(current.id ?? currentId ?? '');
    if (!id) return staffOptions;
    if (staffOptions.some(o => String(o.value) === id)) return staffOptions;
    const isInactive = !(current.is_active === 1 || current.is_active === '1' || current.is_active === true);
    return [
      {
        value: current.id,
        label:
          (`${current.first_name || ''} ${current.last_name || ''}`.trim() ||
            current.email ||
            `User #${current.id}`) + (isInactive ? ' (Inactive)' : ''),
        phone: current.phone || ''
      },
      ...staffOptions
    ];
  };

  const nurseOptions = buildOptions(nurse, nurseUserId);
  const cmOptions = buildOptions(caseManager, caseManagerUserId);

  const resolveValue = (options, id) => {
    if (id == null || id === '') return null;
    return options.find(o => String(o.value) === String(id)) || null;
  };

  return (
    <div className="p-3 mb-4 border rounded bg-light-subtle">
      <h5 className="mb-3 fw-bold">Shared Living Staffing</h5>
      <Row className="mb-3 g-3">
        <Col md={6}>
          <StaleAssignmentBanner roleLabel="Nurse" staff={nurse} />
          <Form.Group>
            <Form.Label>Nurse</Form.Label>
            <ThemedSelect
              isClearable
              isLoading={loading}
              placeholder={loading ? 'Loading staff...' : 'Select a nurse'}
              classNamePrefix="react-select"
              className={errors.nurse ? 'is-invalid' : ''}
              options={nurseOptions}
              value={resolveValue(nurseOptions, nurseUserId)}
              onChange={opt => onChangeNurse(opt ? opt.value : null)}
            />
            {errors.nurse && (
              <Form.Control.Feedback type="invalid" className="d-block">
                {errors.nurse}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>
        <Col md={6}>
          <StaleAssignmentBanner roleLabel="Case Manager" staff={caseManager} />
          <Form.Group>
            <Form.Label>Case Manager</Form.Label>
            <ThemedSelect
              isClearable
              isLoading={loading}
              placeholder={loading ? 'Loading staff...' : 'Select a case manager'}
              classNamePrefix="react-select"
              className={errors.case_manager ? 'is-invalid' : ''}
              options={cmOptions}
              value={resolveValue(cmOptions, caseManagerUserId)}
              onChange={opt => onChangeCaseManager(opt ? opt.value : null)}
            />
            {errors.case_manager && (
              <Form.Control.Feedback type="invalid" className="d-block">
                {errors.case_manager}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>
      </Row>
      {staffOptions.length === 0 && !loading && (
        <div className="text-500 fs-11">
          No role 5 staff with SharedLiving facility access are available in this group.
        </div>
      )}
    </div>
  );
};

export default NurseCasemanagerPickers;
